import logo from '../assets/yellow-dots-logo.png'

/**
 * The Yellow Dots mark on its own, drawn in CSS.
 *
 * <p>Kept as CSS rather than a crop of the logo image because it is used at small sizes where a
 * scaled-down JPEG goes soft, and it inherits the page background instead of carrying the white
 * one baked into the supplied file.
 */
export function Dot({ size = 22, className = '' }: { size?: number; className?: string }) {
  return (
    <span
      className={`inline-block rounded-full bg-sun-400 ${className}`}
      style={{ width: size, height: size, boxShadow: `inset 0 0 0 ${Math.max(2, size / 5)}px #1a1812` }}
      aria-hidden
    />
  )
}

/**
 * The full lockup: dot, "YELLOW DOTS AI", and the mySATHI Foundation line.
 *
 * <p>One image rather than markup, because the wordmark, the superscript AI and the seeded-by line
 * are a single designed piece - rebuilding them in CSS would drift from the brand file the moment
 * either changed.
 *
 * <p>{@code light} still falls back to the CSS mark plus text rather than the image. The file is
 * an RGBA PNG, so it may well sit cleanly on a dark panel - but "has an alpha channel" is not the
 * same as "has a transparent background", and a white block on navy is a worse failure than a
 * slightly different mark. Switch it over once someone has looked at it on a dark surface.
 */
/**
 * Two sizes, because one does not fit both places it appears.
 *
 * <p>The lockup is about 3.6 times wider than it is tall, and the sidebar rail gives it only
 * ~216px. `rail` is what fits there; `page` is for the sign-in, registration and onboarding
 * screens, which have several hundred pixels to spare and where the logo is the first thing
 * anyone sees.
 *
 * <p>The old single size was 36px tall, at which the "Seeded by mySATHI Foundation" line was
 * present but not actually readable.
 */
const SIZES = {
  rail: 'h-11 sm:h-12', // ~173px wide at 48px tall - comfortable in the 216px rail
  page: 'h-14 sm:h-16', // ~230px at 64px - the auth column is 384px, so there is room
} as const

export function Logo({
  light = false,
  size = 'rail',
}: {
  light?: boolean
  size?: keyof typeof SIZES
}) {
  if (light) {
    return (
      <span className="inline-flex items-center gap-2 font-extrabold tracking-tight">
        <Dot size={20} />
        <span className="text-white">YellowDots</span>
      </span>
    )
  }

  return (
    <img
      src={logo}
      // The alt carries the wordmark, so a screen reader or a blocked image still says the brand.
      alt="YellowDots AI - seeded by mySATHI Foundation"
      // Height-driven with max-w-full as the guard: in a container narrower than the chosen
      // height implies, object-contain shrinks it proportionally rather than letting it overflow.
      //
      // mix-blend-multiply hides the file's white background. Multiply leaves anything already
      // white showing whatever is behind it, so the visible rectangle around the lockup
      // disappears on the cream pages without touching the mark or the wordmark. It is why the
      // `light` variant still uses the CSS mark: on a dark panel this would turn the artwork
      // black instead.
      className={`${SIZES[size]} w-auto max-w-full object-contain mix-blend-multiply`}
    />
  )
}
